import { SparklesIcon } from "@heroicons/react/outline";
import { useState } from "react";
import type { Dispatch, SetStateAction } from "react";

import { formatDurationLabel } from "./SpanDetails/utils";
import { findSpanById } from "./utils";

import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useGenerateTraceDescription, useTraceDescription } from "@/queries/traces";
import type { TraceDescriptionStats } from "@/queries/traces";
import type { SpanNode } from "@/types/spans";

const ToolNotes = ({
  tool,
  errorSpanIds,
  slowestCall,
  onJumpToSpan,
}: {
  tool: TraceDescriptionStats["tools"][number];
  errorSpanIds: string[];
  slowestCall: TraceDescriptionStats["slowest_call"];
  onJumpToSpan: (spanId: string) => void;
}) => {
  const [errorCycleIndex, setErrorCycleIndex] = useState(0);
  const isSlowest = slowestCall?.tool === tool.name;
  const failedBadge = tool.error_count ? (
    <Badge variant={BadgeVariant.RED} size={BadgeSize.XS} text="failed" />
  ) : null;
  const nextErrorPosition = errorCycleIndex % Math.max(errorSpanIds.length, 1);
  const jumpToNextError = () => {
    onJumpToSpan(errorSpanIds[nextErrorPosition]);
    setErrorCycleIndex(nextErrorPosition + 1);
  };
  const failedTitle =
    errorSpanIds.length > 1
      ? `Jump to failed call ${nextErrorPosition + 1} of ${errorSpanIds.length} — click again for the next`
      : "Jump to the failed call";
  return (
    <span className="flex flex-wrap items-center gap-1">
      {failedBadge && errorSpanIds.length ? (
        <button type="button" onClick={jumpToNextError} title={failedTitle}>
          {failedBadge}
        </button>
      ) : (
        failedBadge
      )}
      {isSlowest && slowestCall ? (
        <button
          type="button"
          onClick={() => onJumpToSpan(slowestCall.span_id)}
          title={`Jump to the slowest call (${formatDurationLabel(slowestCall.duration_ms)})`}
        >
          <Badge variant={BadgeVariant.ORANGE} size={BadgeSize.XS} text="slowest" />
        </button>
      ) : null}
      {tool.mocked ? (
        <Badge variant={BadgeVariant.AMBER} size={BadgeSize.XS} text="mocked" />
      ) : null}
      {tool.stop ? <Badge variant={BadgeVariant.GRAY} size={BadgeSize.XS} text="stop" /> : null}
    </span>
  );
};

interface TraceSummaryPillsProps {
  traceId?: string;
  flattenedSpans: SpanNode[];
  setSelectedSpan: Dispatch<SetStateAction<SpanNode | null>>;
}

const TraceSummaryPills = ({
  traceId,
  flattenedSpans,
  setSelectedSpan,
}: TraceSummaryPillsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const authContext = useAuth();
  const { activeWorkspaceId } = useUser();
  const userToken = authContext!.userToken!;
  const workspaceId = activeWorkspaceId ?? undefined;
  const { data, isLoading } = useTraceDescription({ userToken, traceId, workspaceId });
  const generateDescription = useGenerateTraceDescription({ userToken, traceId, workspaceId });

  if (isLoading) {
    return <Skeleton width="6rem" height="1.25rem" />;
  }

  if (!data?.success) {
    return null;
  }

  const { stats } = data;
  const hasDescription = data.description !== null && data.description !== undefined;
  // Compare against the live span list (it polls while the trace is open) so a
  // chat appending to this trace marks the summary stale without a page reload.
  const liveSpanCount = flattenedSpans.length;
  const isStale = hasDescription && liveSpanCount > 0 && data.span_count !== liveSpanCount;
  const needsGeneration = !hasDescription || isStale;

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && needsGeneration && !generateDescription.isLoading) {
      generateDescription.mutate();
    }
  };

  const selectSpanById = (spanId: string) => {
    const target = findSpanById(flattenedSpans, spanId);
    if (target) {
      setSelectedSpan(target);
    }
  };

  const renderPopoverBody = () => {
    if (generateDescription.isLoading) {
      const toolNames = new Set(
        flattenedSpans
          .map(span => span.attributes?.["tool.name"])
          .filter((name): name is string => typeof name === "string"),
      );
      const errorSpanCount = flattenedSpans.filter(
        span =>
          span.status?.status_code === "StatusCode.ERROR" ||
          span.events?.some(event => event.name === "exception"),
      ).length;
      const toolRowWidths: [string, string][] = [
        ["8.5rem", "4.5rem"],
        ["8rem", "3rem"],
        ["7rem", "5rem"],
      ].slice(0, Math.min(toolNames.size, 3)) as [string, string][];
      return (
        <div className="flex flex-col gap-y-3">
          <div className="flex flex-col gap-y-1.5">
            <Skeleton height="0.875rem" />
            <Skeleton width="97%" height="0.875rem" />
            <Skeleton width="88%" height="0.875rem" />
            <Skeleton width="55%" height="0.875rem" />
          </div>
          {toolRowWidths.length ? (
            <div>
              <h4 className="mb-1 text-xs font-medium uppercase text-gray-500">Tools</h4>
              <div className="flex items-center justify-between gap-2 border-b border-gray-100 pb-1.5">
                <Skeleton width="2.5rem" height="0.75rem" />
                <Skeleton width="5.5rem" height="0.75rem" />
              </div>
              <div className="mt-2 flex flex-col gap-y-2.5">
                {toolRowWidths.map(([nameWidth, notesWidth]) => (
                  <div key={nameWidth} className="flex items-center justify-between gap-2">
                    <Skeleton width={nameWidth} height="0.875rem" />
                    <Skeleton width={notesWidth} height="0.875rem" />
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {errorSpanCount ? (
            <div>
              <h4 className="mb-1 text-xs font-medium uppercase text-gray-500">Errors</h4>
              <div className="flex flex-col gap-y-1">
                {Array.from({ length: Math.min(errorSpanCount, 2) }, (_, block) => (
                  <Skeleton key={block} height="3rem" />
                ))}
              </div>
            </div>
          ) : null}
          <div className="border-t border-gray-100 pt-2">
            <Skeleton width="7rem" height="0.75rem" />
          </div>
        </div>
      );
    }
    if (generateDescription.isError) {
      return <p className="text-gray-500">Unable to generate a summary for this trace.</p>;
    }
    return (
      <>
        {data.description ? <p className="leading-5 text-gray-700">{data.description}</p> : null}
        {stats?.tools.length ? (
          <div className="mt-3">
            <h4 className="mb-1 text-xs font-medium uppercase text-gray-500">Tools</h4>
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead className="h-7 px-2">Tool</TableHead>
                  <TableHead className="h-7 px-2 text-right">Calls</TableHead>
                  <TableHead className="h-7 px-2">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.tools.map(tool => (
                  <TableRow key={tool.name}>
                    <TableCell className="max-w-40 truncate px-2 py-1.5 font-mono text-gray-700">
                      {tool.name}
                    </TableCell>
                    <TableCell className="px-2 py-1.5 text-right text-gray-600">
                      {tool.count}
                    </TableCell>
                    <TableCell className="px-2 py-1.5 text-gray-500">
                      <ToolNotes
                        tool={tool}
                        errorSpanIds={stats.errors
                          .filter(error => error.span_name === tool.name)
                          .map(error => error.span_id)}
                        slowestCall={stats.slowest_call}
                        onJumpToSpan={selectSpanById}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : null}
        {stats?.errors.length ? (
          <div className="mt-3">
            <h4 className="mb-1 text-xs font-medium uppercase text-gray-500">Errors</h4>
            <ul className="space-y-1">
              {stats.errors.map(error => (
                <li key={error.span_id}>
                  <button
                    type="button"
                    onClick={() => selectSpanById(error.span_id)}
                    className="w-full rounded border border-red-200 bg-red-50 px-2 py-1 text-left transition-colors hover:bg-red-100"
                  >
                    <span className="font-mono text-xs text-red-700">{error.span_name}</span>
                    {error.message ? (
                      <span className="mt-0.5 line-clamp-2 block text-xs text-red-600">
                        {error.message}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {stats && (stats.total_tokens > 0 || stats.total_price > 0) ? (
          <p className="mt-3 border-t border-gray-100 pt-2 text-xs text-gray-500">
            {stats.total_tokens.toLocaleString()} tokens · ${stats.total_price.toFixed(4)}
          </p>
        ) : null}
      </>
    );
  };

  return (
    <div className="flex shrink-0 items-center">
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex h-5 items-center gap-1 rounded-full border border-gray-300 bg-white px-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-50"
          >
            <SparklesIcon className="h-3.5 w-3.5 text-gray-400" />
            Summary
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-96 text-sm">
          {renderPopoverBody()}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default TraceSummaryPills;
